import { createContext, ReactNode, useMemo } from 'react';

export interface HomePackItemContextValue {
    /** Zero-based index of this item within its home pack row's carousel. */
    rowItemIndex: number;
    /**
     * Display name of the item (e.g. guide title, resource title, banner
     * title). When provided, downstream events attach it as PackItemName.
     */
    packItemName?: string;
}

export const HomePackItemContext = createContext<
    HomePackItemContextValue | undefined
>(undefined);

/**
 * Provides per-item context for a single item within a home pack row.
 *
 * Note: unlike `HomePackRowIndexProvider`, this intentionally does NOT
 * install an `AnalyticsEventPropsProvider`. Item-level analytics props
 * (`RowItemIndex`, `PackItemName`) flow explicitly via `trackProps` from
 * composers like `DynamicPackSectionItem` — keeping cards generic instead
 * of having them silently inherit home-row item attribution on every
 * button click.
 */
export function HomePackItemProvider({
    rowItemIndex,
    packItemName,
    children,
}: {
    rowItemIndex: number;
    packItemName?: string;
    children: ReactNode;
}) {
    const value = useMemo(
        () => ({ rowItemIndex, packItemName }),
        [rowItemIndex, packItemName]
    );
    return (
        <HomePackItemContext.Provider value={value}>
            {children}
        </HomePackItemContext.Provider>
    );
}
